import {
  MAX_REGISTRATION_NUMBER_LENGTH,
  checkEconomicCode,
  checkLegalId,
  checkRegistrationNumber,
  isLegalId,
  isNationalId,
  toEnglishDigits,
  type EconomicCodeKind,
  type IdentifierIssue,
} from '../../../shared/iranian-identifiers';

export type CompanyFieldKey =
  | 'name'
  | 'legalName'
  | 'nationalId'
  | 'economicCode'
  | 'registrationNumber';

export type CompanyFormValues = Record<CompanyFieldKey, string>;
export const companyFieldKeys: CompanyFieldKey[] = ['name', 'legalName', 'nationalId', 'economicCode', 'registrationNumber'];

export { MAX_REGISTRATION_NUMBER_LENGTH, isLegalId, toEnglishDigits };

/** Smart numeric input: normalize first, then keep only allowed digits. */
export function digitsOnly(value: string, maxLength: number): string {
  return toEnglishDigits(value).replace(/\D/g, '').slice(0, maxLength);
}

export function normalizeHumanText(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

export function normalizeCompanyField(field: CompanyFieldKey, value: string): string {
  switch (field) {
    case 'nationalId': return digitsOnly(value, 11);
    case 'economicCode': return digitsOnly(value, 14);
    case 'registrationNumber': return digitsOnly(value, MAX_REGISTRATION_NUMBER_LENGTH);
    default: return value;
  }
}

export function finalizeCompanyField(field: CompanyFieldKey, value: string): string {
  return field === 'name' || field === 'legalName'
    ? normalizeHumanText(value)
    : normalizeCompanyField(field, value);
}

export const companyFieldHints: Record<CompanyFieldKey, string> = {
  name: 'نام کوتاهی که در فهرست‌ها و گزارش‌ها دیده می‌شود.',
  legalName: 'نام کامل مطابق آخرین آگهی یا مدرک ثبتی.',
  nationalId: '۱۱ رقم؛ رقم کنترل بررسی می‌شود. ارقام فارسی و عربی خودکار تبدیل می‌شوند.',
  economicCode: 'برای شرکت‌ها همان شناسه ملی ۱۱ رقمی است؛ اشخاص حقیقی کد ۱۴ رقمی دارند.',
  registrationNumber: 'فقط عدد، مطابق آگهی ثبت شرکت؛ معمولاً ۳ تا ۶ رقم.',
};

const legalIdMessages: Record<IdentifierIssue, string> = {
  not_numeric: 'شناسه ملی فقط از رقم تشکیل می‌شود.',
  length: 'شناسه ملی شرکت باید دقیقاً ۱۱ رقم باشد.',
  repeated_digit: 'شناسه ملی نمی‌تواند تکرار یک رقم باشد.',
  blank_serial: 'این شناسه ملی به هیچ شرکتی تعلق ندارد؛ بخش اختصاصی آن صفر است.',
  checksum: 'رقم کنترل شناسه ملی نادرست است؛ عدد را با آگهی ثبت مطابقت دهید.',
};

const economicCodeMessages: Record<IdentifierIssue, string> = {
  not_numeric: 'کد اقتصادی فقط از رقم تشکیل می‌شود.',
  length: 'کد اقتصادی باید ۱۱ رقم (شناسه ملی شرکت)، ۱۴ رقم (اشخاص حقیقی) یا ۱۲ رقم (کد قدیمی) باشد.',
  repeated_digit: 'کد اقتصادی نمی‌تواند تکرار یک رقم باشد.',
  blank_serial: 'این کد اقتصادی نامعتبر است؛ بخش اختصاصی آن صفر است.',
  checksum: 'کد اقتصادی ۱۱ رقمی باید شناسه ملی معتبر شرکت باشد؛ رقم کنترل آن نادرست است.',
};

const registrationNumberMessages: Record<IdentifierIssue, string> = {
  not_numeric: 'شماره ثبت فقط از رقم تشکیل می‌شود.',
  length: `شماره ثبت نمی‌تواند بیشتر از ${MAX_REGISTRATION_NUMBER_LENGTH} رقم باشد.`,
  repeated_digit: 'شماره ثبت معتبر نیست.',
  blank_serial: 'شماره ثبت نمی‌تواند صفر باشد.',
  checksum: 'شماره ثبت معتبر نیست.',
};

export function validateCompanyField(field: CompanyFieldKey, rawValue: string): string {
  const value = rawValue.trim();
  switch (field) {
    case 'name':
      if (value.length < 2) return 'نام نمایشی باید دست‌کم ۲ نویسه باشد.';
      if (value.length > 160) return 'نام نمایشی نمی‌تواند بیشتر از ۱۶۰ نویسه باشد.';
      return '';
    case 'legalName':
      return value.length > 240 ? 'نام قانونی نمی‌تواند بیشتر از ۲۴۰ نویسه باشد.' : '';
    case 'nationalId': {
      if (!value) return '';
      const result = checkLegalId(value);
      if (result.ok) return '';
      // A sole proprietor's ten-digit کد ملی is the mistake people actually make here.
      if (result.issue === 'length' && isNationalId(value)) {
        return 'این عدد کد ملی است، نه شناسه ملی؛ شناسه ملی اشخاص حقوقی ۱۱ رقم است.';
      }
      return legalIdMessages[result.issue!];
    }
    case 'economicCode': {
      if (!value) return '';
      const result = checkEconomicCode(value);
      return result.ok ? '' : economicCodeMessages[result.issue!];
    }
    case 'registrationNumber': {
      if (!value) return '';
      const result = checkRegistrationNumber(value);
      return result.ok ? '' : registrationNumberMessages[result.issue!];
    }
  }
}

const economicCodeKindLabels: Record<EconomicCodeKind, string> = {
  'legal-entity': 'کد اقتصادی شخص حقوقی — همان شناسه ملی شرکت.',
  'civil-partnership': 'کد اقتصادی مشارکت مدنی.',
  'natural-person': 'کد اقتصادی شخص حقیقی، ساخته‌شده روی کد ملی.',
  'foreign-or-branch': 'کد ۱۴ رقمی؛ کد ملی ایرانی در آن نبود — اتباع غیرایرانی یا پرونده شعبه.',
  legacy: 'کد ۱۲ رقمی قدیمی؛ هنوز روی اسناد بایگانی دیده می‌شود.',
};

/**
 * What to say once a value passes. A green tick alone only claims "valid" —
 * this names what the number actually is, which is the part a bookkeeper needs.
 */
export function describeCompanyField(field: CompanyFieldKey, rawValue: string): string {
  const value = rawValue.trim();
  if (!value) return '';
  switch (field) {
    case 'nationalId':
      return checkLegalId(value).ok ? 'شناسه ملی معتبر است؛ رقم کنترل خوانده شد.' : '';
    case 'economicCode': {
      const result = checkEconomicCode(value);
      return result.ok && result.kind ? economicCodeKindLabels[result.kind] : '';
    }
    default:
      return '';
  }
}

export function validateCompanyForm(values: CompanyFormValues): Partial<Record<CompanyFieldKey, string>> {
  return companyFieldKeys.reduce<Partial<Record<CompanyFieldKey, string>>>((errors, field) => {
    const error = validateCompanyField(field, values[field]);
    if (error) errors[field] = error;
    return errors;
  }, {});
}

export function finalizeCompanyForm<T extends CompanyFormValues>(values: T): T {
  return {
    ...values,
    name: finalizeCompanyField('name', values.name),
    legalName: finalizeCompanyField('legalName', values.legalName),
    nationalId: finalizeCompanyField('nationalId', values.nationalId),
    economicCode: finalizeCompanyField('economicCode', values.economicCode),
    registrationNumber: finalizeCompanyField('registrationNumber', values.registrationNumber),
  };
}
